/**
 * Manages manuscript collection persistence via localStorage.
 *
 * Manuscripts are permanent collectibles — once found, they persist forever
 * and are browsable from the ManuscriptsScene on the title menu.
 *
 * Persistence layer:
 *   - MANUSCRIPTS_KEY: Set of collected manuscript IDs — persists forever.
 */

import { MANUSCRIPTS, MANUSCRIPT_COUNT } from '../data/ManuscriptData.js';

const MANUSCRIPTS_KEY = 'lost-in-labyrinth-manuscripts';
const MANUSCRIPTS_READ_KEY = 'lost-in-labyrinth-manuscripts-read';

/**
 * Get the set of collected manuscript IDs from localStorage.
 *
 * @returns {Set<number>} Set of manuscript IDs the player has found.
 */
export function getCollectedManuscripts() {
  try {
    const raw = localStorage.getItem(MANUSCRIPTS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      console.warn('Corrupted manuscript data detected, discarding.');
      localStorage.removeItem(MANUSCRIPTS_KEY);
      return new Set();
    }
    // Filter to only valid IDs (1-MANUSCRIPT_COUNT)
    const validIds = parsed.filter(
      (id) => typeof id === 'number' && Number.isInteger(id) && id >= 1 && id <= MANUSCRIPT_COUNT
    );
    return new Set(validIds);
  } catch (err) {
    console.warn('Failed to load manuscript data:', err);
    return new Set();
  }
}

/**
 * Mark a manuscript as collected and persist to localStorage.
 *
 * @param {number} manuscriptId - The manuscript ID (1-30) to mark as collected.
 * @returns {boolean} True if the manuscript was newly collected, false if already owned.
 */
export function collectManuscript(manuscriptId) {
  const collected = getCollectedManuscripts();
  if (collected.has(manuscriptId)) return false;
  collected.add(manuscriptId);
  _saveCollected(collected);
  return true;
}

/**
 * Check whether a specific manuscript has been collected.
 *
 * @param {number} manuscriptId - The manuscript ID (1-30).
 * @returns {boolean}
 */
export function hasManuscript(manuscriptId) {
  return getCollectedManuscripts().has(manuscriptId);
}

/**
 * Get the number of manuscripts collected.
 *
 * @returns {number}
 */
export function getCollectedCount() {
  return getCollectedManuscripts().size;
}

/**
 * Check whether all manuscripts have been collected.
 *
 * @returns {boolean}
 */
export function allManuscriptsCollected() {
  return getCollectedCount() >= MANUSCRIPT_COUNT;
}

/**
 * Pick a random uncollected manuscript for spawning on a maze level.
 * Returns null if all manuscripts have been collected.
 *
 * @returns {{id: number, arc: number, title: string, body: string}|null}
 *   The full manuscript object, or null if all are collected.
 */
export function pickUncollectedManuscript() {
  const collected = getCollectedManuscripts();
  const uncollected = MANUSCRIPTS.filter((m) => !collected.has(m.id));
  if (uncollected.length === 0) return null;
  const idx = Math.floor(Math.random() * uncollected.length);
  return uncollected[idx];
}

/**
 * Get the full manuscript object by ID.
 *
 * @param {number} manuscriptId - The manuscript ID (1-30).
 * @returns {{id: number, arc: number, title: string, body: string}|null}
 */
export function getManuscriptById(manuscriptId) {
  return MANUSCRIPTS.find((m) => m.id === manuscriptId) || null;
}

/**
 * Get the set of manuscript IDs that the player has already read (opened
 * in ManuscriptsScene). Used to distinguish "new" from "seen" manuscripts.
 *
 * @returns {Set<number>} Set of read manuscript IDs.
 */
export function getReadManuscripts() {
  try {
    const raw = localStorage.getItem(MANUSCRIPTS_READ_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      localStorage.removeItem(MANUSCRIPTS_READ_KEY);
      return new Set();
    }
    const validIds = parsed.filter(
      (id) => typeof id === 'number' && Number.isInteger(id) && id >= 1 && id <= MANUSCRIPT_COUNT
    );
    return new Set(validIds);
  } catch (err) {
    console.warn('Failed to load manuscript read data:', err);
    return new Set();
  }
}

/**
 * Mark a manuscript as read (player opened it in ManuscriptsScene).
 *
 * @param {number} manuscriptId - The manuscript ID (1-30).
 * @returns {boolean} True if the manuscript was newly marked as read.
 */
export function markManuscriptRead(manuscriptId) {
  const readSet = getReadManuscripts();
  if (readSet.has(manuscriptId)) return false;
  readSet.add(manuscriptId);
  _saveReadSet(readSet);
  return true;
}

/**
 * Check whether a collected manuscript is "new" (collected but not yet
 * opened/read in ManuscriptsScene).
 *
 * @param {number} manuscriptId - The manuscript ID (1-30).
 * @returns {boolean} True if collected but not yet read.
 */
export function isManuscriptNew(manuscriptId) {
  const collected = getCollectedManuscripts();
  if (!collected.has(manuscriptId)) return false;
  const readSet = getReadManuscripts();
  return !readSet.has(manuscriptId);
}

/**
 * Persist the read set to localStorage.
 * @param {Set<number>} readSet
 * @private
 */
function _saveReadSet(readSet) {
  try {
    const arr = Array.from(readSet).sort((a, b) => a - b);
    localStorage.setItem(MANUSCRIPTS_READ_KEY, JSON.stringify(arr));
  } catch (err) {
    console.warn('Failed to save manuscript read data:', err);
  }
}

/**
 * Persist the collected set to localStorage.
 * @param {Set<number>} collected
 * @private
 */
function _saveCollected(collected) {
  try {
    const arr = Array.from(collected).sort((a, b) => a - b);
    localStorage.setItem(MANUSCRIPTS_KEY, JSON.stringify(arr));
  } catch (err) {
    console.warn('Failed to save manuscript data:', err);
  }
}
